import { Component, OnInit } from '@angular/core';
import { CallLengthRange, Filter, FilterItem, FiltersEditedItems, FilterTag, Page } from '../../shared/models/Contact';
import { ContactsService } from '../../shared/services/contacts.service';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import {
  getCallLengthFormSelector,
  getEditedFilterItemsSelector,
  getEditedFiltersSelector,
  getOpenedFilterItemSelector,
  getTempPageSelector
} from '../../contacts.reducer';
import {
  AddTagAction,
  GetDataAction,
  GetFilterItemsSuccessAction,
  RemoveTagAction,
  RevertEditedFilterChanges,
  SaveCallRangeFilterFormAction,
  SetFilterTagsAction,
  UpdateTempPageWithItemsAction,
  UpdateTempTagAction
} from '../../contacts.actions';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormBuilder, FormGroup } from '@angular/forms';
import { callLengthMatcher } from './call-length-matcher';
import { FilterService } from '../../shared/services/filter.service';

@Component({
  selector: 'cc-filter-call-length',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          opacity: 1,
          top: 0,
          right: 0
        })
      ),
      state(
        'closed',
        style({
          opacity: 1,
          top: 0,
          right: '-100%'
        })
      ),
      transition('open => closed', [animate('1s ease')]),
      transition('closed => open', [animate('0.5s')])
    ])
  ],
  templateUrl: './filter-call-length.component.html',
  styleUrls: ['./filter-call-length.component.scss']
})
export class FilterCallLengthComponent implements OnInit {
  public callLengthFilterOpened: boolean;
  public rangeForm: FormGroup;
  public filtersOpened: boolean;
  public filter = 'callLengths';
  public currentTempFilters: Filter;
  public currentPageWithFilters: Page;
  public isFilteringReady: boolean;
  public editedFiltersList: string[];
  public name: string;
  public rangesList: FilterItem[];
  private currentCallLengthForm: any;
  private validationErrored: string[];

  constructor(
    public contactsService: ContactsService,
    private store: Store<IState>,
    private fb: FormBuilder,
    public filterService: FilterService
  ) {}

  ngOnInit() {
    this.contactsService.toggleOpeningFilters.subscribe((opener: boolean) => {
      this.filtersOpened = opener;
    });

    this.contactsService.toggleOpeningCallLengthsFilters.subscribe(opener => {
      this.callLengthFilterOpened = opener;
      if (opener) {
        this.filterService.getFilterSnapshot();
      }
    });

    this.store.select(getEditedFilterItemsSelector).subscribe((response: FiltersEditedItems) => {
      if (response && response.callLengths) {
        this.rangesList = response.callLengths;

        this.initForms();
      }
    });

    this.store.select(getEditedFiltersSelector).subscribe((editedFiltersList: string[]) => {
      this.editedFiltersList = editedFiltersList;
    });

    this.store.select(getTempPageSelector).subscribe((page: Page) => {
      if (page) {
        this.currentPageWithFilters = page;
        this.currentTempFilters = page.filter;
      }
    });

    this.store.select(getCallLengthFormSelector).subscribe((callLengthForm: any) => {
      if (callLengthForm) {
        this.currentCallLengthForm = callLengthForm;
      }
    });

    this.contactsService.onCallRangeTagFilterOff.subscribe((tag: FilterTag) => {
      if (tag && !!this.rangesList.length) {
        const rangeItemToRemove = this.rangesList.find(item => item.range.name === tag.callLengthsRange.name);
        this.removeRange(rangeItemToRemove);
        this.rangesList.forEach(filterItem => this.filteringByCallLengthRange(filterItem, 'callLengths'));
      }
    });

    this.contactsService.onCallRangeTagCollectionOff.subscribe(() => {
      this.removeRangesCollection();
    });
    this.validationErrored = [];
    this.initForms();
    this.isFilteringReady = true;
  }

  private initForms() {
    this.rangeForm = this.fb.group({
      rangesCollection: this.fb.array(this.createItem())
    });
  }

  createItem(): FormGroup[] {
    return this.rangesList
      ? this.rangesList.map(rangeItem => {
          const recalculated = this.calculateInputsFromRange(
            rangeItem.range.minLengthSeconds,
            rangeItem.range.maxLengthSeconds
          );

          return this.fb.group(
            {
              name: [rangeItem.name],
              from: this.fb.group({
                fromMin: [recalculated.fromMin],
                fromSec: [recalculated.fromSec]
              }),
              to: this.fb.group({
                toMin: [recalculated.toMin],
                toSec: [recalculated.toSec]
              }),
              isFilter:
                this.rangesList && this.rangesList[rangeItem.name]
                  ? [this.rangesList[rangeItem.name].isFilter]
                  : [false]
            },
            { validator: callLengthMatcher }
          );
        })
      : [];
  }

  addNewRange() {
    const indexOfNewGroup = this.rangesList.length + 1;
    this.store.dispatch(new SaveCallRangeFilterFormAction(this.rangeForm.value.rangesCollection));
    const rangesListFilled = this.rangesList.map((rangeItem, index) => {
      rangeItem.range = this.buildCallLengthRange(`RANGE_${index + 1}`);

      return rangeItem;
    });
    const newRange = {
      name: `RANGE_${indexOfNewGroup}`,
      range: {
        name: `RANGE_${indexOfNewGroup}`,
        minLengthSeconds: 0,
        maxLengthSeconds: 3599
      },
      type: this.filter,
      isFilter: false
    };
    const extendedRangesList = [...rangesListFilled, newRange];
    this.updateSwitchView(extendedRangesList);
    this.isFilteringReady = true;
  }

  buildCallLengthRange(rangeFormGroupName: string): CallLengthRange {
    const rangeValues = this.getFormRangeValues(rangeFormGroupName);
    const minLengthSeconds = rangeValues.fromMin * 60 + rangeValues.fromSec;
    const maxLengthSeconds = rangeValues.toMin * 60 + rangeValues.toSec;

    return {
      name: rangeFormGroupName,
      minLengthSeconds: minLengthSeconds,
      maxLengthSeconds: maxLengthSeconds
    };
  }

  calculateInputsFromRange(minRange: number, maxRange: number) {
    return {
      fromMin: Math.floor(minRange / 60),
      fromSec: minRange - Math.floor(minRange / 60) * 60,
      toMin: Math.floor(maxRange / 60),
      toSec: maxRange - Math.floor(maxRange / 60) * 60
    };
  }

  getFormRangeValues(rangeFormGroupName: string) {
    const rangeNumberName = rangeFormGroupName.replace('RANGE_', '');
    const rangeNumber = parseInt(rangeNumberName, 10) - 1;
    const myFormRangesCollectionItem = this.currentCallLengthForm ? this.currentCallLengthForm[rangeNumber] : null;
    const fromMin = myFormRangesCollectionItem ? myFormRangesCollectionItem.from.fromMin : 0;
    const fromSec = myFormRangesCollectionItem ? myFormRangesCollectionItem.from.fromSec : 0;
    const toMin = myFormRangesCollectionItem ? myFormRangesCollectionItem.to.toMin : 0;
    const toSec = myFormRangesCollectionItem ? myFormRangesCollectionItem.to.toSec : 0;

    const minLengthSeconds = fromMin * 60 + fromSec;
    const maxLengthSeconds = toMin * 60 + toSec;

    const recalculated = this.calculateInputsFromRange(minLengthSeconds, maxLengthSeconds);

    return {
      fromMin: recalculated.fromMin,
      fromSec: recalculated.fromSec,
      toMin: recalculated.toMin,
      toSec: recalculated.toSec
    };
  }

  buildCallRangeFilterTag(formRangeName: string): FilterTag {
    if (formRangeName) {
      const formValues = this.getFormRangeValues(formRangeName);
      const fromMin = formValues.fromMin;
      const fromSec = formValues.fromSec;
      const toMin = formValues.toMin;
      const toSec = formValues.toSec;
      const calLengthRange = this.buildCallLengthRange(formRangeName);

      return {
        name: `${fromMin}min ${fromSec}sec - ${toMin}min ${toSec}sec`,
        source: this.filter,
        title: this.contactsService.getFilterTitle(this.filter),
        callLengthsRange: {
          name: formRangeName,
          minLengthSeconds: calLengthRange.minLengthSeconds,
          maxLengthSeconds: calLengthRange.maxLengthSeconds
        }
      };
    }
  }

  buildFilterTagFromRange(range: CallLengthRange): FilterTag {
    const inputs = this.calculateInputsFromRange(range.minLengthSeconds, range.maxLengthSeconds);
    return {
      name: `${inputs.fromMin}min ${inputs.fromSec}sec - ${inputs.toMin}min ${inputs.toSec}sec`,
      source: this.filter,
      title: this.contactsService.getFilterTitle('callLengths'),
      callLengthsRange: {
        name: range.name,
        minLengthSeconds: range.minLengthSeconds,
        maxLengthSeconds: range.maxLengthSeconds
      }
    };
  }

  buildCallRangeFilterItem(formRangeName: string): FilterItem {
    if (formRangeName) {
      const calLengthRange = this.buildCallLengthRange(formRangeName);

      return {
        name: formRangeName,
        type: this.filter,
        isFilter: false,
        range: {
          name: formRangeName,
          minLengthSeconds: calLengthRange.minLengthSeconds,
          maxLengthSeconds: calLengthRange.maxLengthSeconds
        }
      };
    }
  }

  updateRangesListByForm() {
    this.rangesList = this.rangeForm.value.rangesCollection.map(inputGroup => {
      const minLengthSeconds = inputGroup.from.fromMin * 60 + inputGroup.from.fromSec;
      const maxLengthSeconds = inputGroup.to.toMin * 60 + inputGroup.to.toSec;

      return {
        name: inputGroup.name,
        type: this.filter,
        isFilter: false,
        range: {
          name: inputGroup.name,
          minLengthSeconds: minLengthSeconds,
          maxLengthSeconds: maxLengthSeconds
        }
      };
    });
  }

  removeRange(callLengthRange: FilterItem) {
    this.updateRangesListByForm();

    const oldRangesList = this.rangesList;
    const newRangesList = this.rangesList
      .filter(range => range.name !== callLengthRange.name)
      .map((rangeItem, index) => {
        rangeItem.name = rangeItem.name.substring(0, 6) + (index + 1);
        rangeItem.range.name = rangeItem.name;

        return rangeItem;
      });
    this.rangesList = newRangesList;
    const rangeListToRequest = newRangesList.map(item => item.range);
    const tagsList = newRangesList.map(item => {
      return this.buildFilterTagFromRange(item.range);
    });
    this.store.dispatch(new UpdateTempTagAction({ tags: tagsList, source: 'callLengths' }));
    this.updateStoreFilters(this.rangesList, rangeListToRequest, this.editedFiltersList, 'callLengths');
    this.store.dispatch(new SaveCallRangeFilterFormAction(this.rangeForm.value.rangesCollection));
    this.isFilteringReady = oldRangesList !== this.rangesList;
  }

  removeRangesCollection() {
    const oldRangesList = this.rangesList;
    const rangeListToRequest = [];
    this.store.dispatch(new UpdateTempTagAction({ tags: [], source: 'callLengths' }));
    this.store.dispatch(new SetFilterTagsAction());
    this.rangesList = [];
    this.updateStoreFilters(this.rangesList, rangeListToRequest, this.editedFiltersList, 'callLengths');
    this.contactsService.fetchDataAction(this.currentPageWithFilters, 0);
    this.isFilteringReady = oldRangesList !== this.rangesList;
  }

  filteringByCallLengthRange(callLengthRange: FilterItem, openedFilter = 'callLengths') {
    const listToRequest = this.rangesList.map((range, index) => this.buildCallLengthRange(`RANGE_${index + 1}`));
    const newRangeList = this.rangesList.map((range, index) => this.buildCallRangeFilterItem(`RANGE_${index + 1}`));
    this.updateStoreFilters(newRangeList, listToRequest, this.editedFiltersList, this.filter);
    this.store.dispatch(new AddTagAction(this.buildCallRangeFilterTag(callLengthRange.name)));
  }

  updateStoreFilters(rangeList, requestFilterList, editedFilters, filter) {
    this.filterService.updateStoreEditedFiltersList(rangeList, editedFilters, filter);
    this.filterService.updateStoreRequestFilters(requestFilterList, filter);
    this.updateSwitchView(rangeList);
  }

  updateSwitchView(list) {
    this.store.dispatch(new GetFilterItemsSuccessAction({ list: list, filterName: 'callLengths' }));
  }

  backToFilters() {
    this.store.dispatch(new RevertEditedFilterChanges());
    this.contactsService.showSearchFilters();
    this.contactsService.hideCallLengthsFilters();
  }

  filtersResign() {
    this.contactsService.closeAllFilters();
  }

  callLengthsFiltersDone() {
    this.store.dispatch(new SaveCallRangeFilterFormAction(this.rangeForm.value.rangesCollection));
    this.rangesList.forEach(filterItem => this.filteringByCallLengthRange(filterItem, 'callLengths'));
    this.contactsService.showSearchFilters();
    this.contactsService.hideCallLengthsFilters();
    this.contactsService.onDoneFilter();
    this.store.dispatch(new UpdateTempPageWithItemsAction('callLengths'));
  }

  translateFilterName(filter: string) {
    return this.contactsService.getFilterTitle(filter);
  }

  onChangeInput() {
    this.store.dispatch(new SaveCallRangeFilterFormAction(this.rangeForm.value.rangesCollection));
    this.isEveryMaxGraterThanMin();
    this.isFilteringReady = !this.validationErrored.length;
  }

  hasRangeErrors(index: number) {
    return !!this.validationErrored[index];
  }

  isEveryMaxGraterThanMin() {
    this.validationErrored = [];

    this.rangesList.forEach((range, index) => {
      const groupErrored = this.rangeForm
        .get('rangesCollection')
        .get('' + index)
        .hasError('nomatch');
      if (groupErrored) {
        this.validationErrored[index] = range.name;
      }
    });
  }

  exitFilter($event) {
    if ($event.target.className === 'nav-text' && this.filtersOpened && this.callLengthFilterOpened) {
      this.contactsService.closeAllFilters();
    }
  }
}
